import { useState, useEffect, useCallback } from "react"

const BANNER_DISMISSAL_KEY = "rtefn-banner-dismissed"

interface BannerConfig {
  id: string
  version: number
}

export function usePersistedBanner(bannerId: string, version: number = 1) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(BANNER_DISMISSAL_KEY)
      if (!stored) return false

      const dismissedBanners: BannerConfig[] = JSON.parse(stored)
      const bannerConfig = dismissedBanners.find((banner) => banner.id === bannerId && banner.version >= version)

      return Boolean(bannerConfig)
    } catch {
      return false
    }
  })

  const dismissBanner = () => {
    try {
      const stored = localStorage.getItem(BANNER_DISMISSAL_KEY)
      const dismissedBanners: BannerConfig[] = stored ? JSON.parse(stored) : []

      // Remove any existing entries for this banner ID
      const filteredBanners = dismissedBanners.filter((banner) => banner.id !== bannerId)

      // Add the new dismissal
      filteredBanners.push({ id: bannerId, version })

      localStorage.setItem(BANNER_DISMISSAL_KEY, JSON.stringify(filteredBanners))
      setDismissed(true)
    } catch {
      // Fallback to session-only dismissal if localStorage fails
      setDismissed(true)
    }
  }

  const resetBanner = useCallback(() => {
    try {
      const stored = localStorage.getItem(BANNER_DISMISSAL_KEY)
      if (stored) {
        const dismissedBanners: BannerConfig[] = JSON.parse(stored)
        const filteredBanners = dismissedBanners.filter((banner) => banner.id !== bannerId)
        localStorage.setItem(BANNER_DISMISSAL_KEY, JSON.stringify(filteredBanners))
      }
      setDismissed(false)
    } catch {
      setDismissed(false)
    }
  }, [bannerId])

  // Check for version changes on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(BANNER_DISMISSAL_KEY)
      if (stored) {
        const dismissedBanners: BannerConfig[] = JSON.parse(stored)
        const bannerConfig = dismissedBanners.find((banner) => banner.id === bannerId)

        // If banner exists but version is outdated, reset dismissal
        if (bannerConfig && bannerConfig.version < version) {
          resetBanner()
        }
      }
    } catch {
      // Ignore errors
    }
  }, [bannerId, version, resetBanner])

  return {
    dismissed,
    dismissBanner,
    resetBanner,
  }
}
